/**
 * MDR ROI Calculator
 * Calculates cost of breach, SOC costs vs MDR costs, and potential savings
 * 
 * @typedef {'USA' | 'Canada' | 'United Kingdom' | 'European Union' | 'Australia'} Country
 * @typedef {'Medium' | 'High'} RiskType
 */

// ----- Constants -----

/** @type {Object.<Country, { currencyCode: string, currencySymbol: string, loadedSalary: number, mdrPricePerEndpoint: number, exchangeRate: number }>} */
const COUNTRY_CONFIGS = {
    'USA': {
        currencyCode: 'USD',
        currencySymbol: '$',
        loadedSalary: 129377,
        mdrPricePerEndpoint: 99,
        exchangeRate: 1 // Base currency
    },
    'Canada': {
        currencyCode: 'CAD',
        currencySymbol: '$',
        loadedSalary: 109875,
        mdrPricePerEndpoint: 127.57,
        exchangeRate: 1.37 // 2/23/26 rate
    },
    'United Kingdom': {
        currencyCode: 'GBP',
        currencySymbol: '£',
        loadedSalary: 75401,
        mdrPricePerEndpoint: 81.84,
        exchangeRate: 0.74  // 2/23/26 rate
    },
    'European Union': {
        currencyCode: 'EUR',
        currencySymbol: '€',
        loadedSalary: 68931,
        mdrPricePerEndpoint: 96.23,
        exchangeRate: 0.85 // 2/23/26 rate
    },
    'Australia': {
        currencyCode: 'AUD',
        currencySymbol: '$',
        loadedSalary: 137418,
        mdrPricePerEndpoint: 142.51,
        exchangeRate: 1.42  // 2/23/26 rate
    }
};

/** @type {Object.<string, Country>} Maps select values to Country keys */
const COUNTRY_SELECT_MAP = {
    'US': 'USA',
    'CA': 'Canada',
    'UK': 'United Kingdom',
    'EU': 'European Union',
    'AU': 'Australia'
};

const MEDIUM_RISK_RATE = 0.01; // 1.00%
const HIGH_RISK_RATE = 0.03; // 3.00%

// Default calculator values
const DEFAULTS = {
    country: 'USA',
    riskType: 'Medium',
    annualRevenue: 100000000,
    endpoints: 500,
    analystCount: 5
};

const INPUT_LIMITS = {
    annualRevenue: { min: 1_000_000, max: 10_000_000_000 },
    endpoints: { min: 10, max: 5000 },
    analystCount: { min: 1, max: 25 },
    analystCompensation: { min: 40_000, max: 300_000 }
};

const INFRASTRUCTURE_COST_BANDS = [
    { min: 100, max: 249, cost: 25_000 },
    { min: 250, max: 499, cost: 50_000 },
    { min: 500, max: 999, cost: 100_000 },
    { min: 1000, max: Infinity, cost: 250_000 }
];

// ----- Utility Functions -----

/**
 * Get default analyst count based on endpoint range
 * @param {number} endpoints
 * @returns {number} default analysts count or NaN if endpoints out of range
 */
function defaultAnalystCount(endpoints) {
    if (endpoints >= 1 && endpoints <= 50) return 3;
    if (endpoints >= 51 && endpoints <= 500) return 5;
    if (endpoints >= 501 && endpoints <= 1000) return 6;
    if (endpoints >= 1001 && endpoints <= 1500) return 7;
    if (endpoints >= 1501 && endpoints <= 2000) return 8;
    if (endpoints >= 2001 && endpoints <= 2500) return 9;
    if (endpoints >= 2501 && endpoints <= 3000) return 10;
    if (endpoints >= 3001 && endpoints <= 5000) return 12;
    return NaN;
}

/**
 * Determine annual infrastructure cost based on endpoint volume
 * @param {number} endpoints
 * @param {number} exchangeRate
 * @returns {number}
 */
function calculateInfrastructureCost(endpoints, exchangeRate = 1) {
    if (!Number.isFinite(endpoints)) return 0;
    const absEndpoints = Math.max(0, endpoints);
    for (const band of INFRASTRUCTURE_COST_BANDS) {
        if (absEndpoints >= band.min && absEndpoints <= band.max) {
            const rate = Number.isFinite(exchangeRate) ? exchangeRate : 1;
            return band.cost * rate;
        }
    }
    return 0;
}

/**
 * Calculate risk/cost of an unprotected business breach
 * @param {number} annualRevenue
 * @param {RiskType} riskType
 * @returns {number}
 */
function calculateCostOfBreach(annualRevenue, riskType) {
    const rate = riskType === 'Medium' ? MEDIUM_RISK_RATE : HIGH_RISK_RATE;
    return annualRevenue * rate;
}

/**
 * Calculate cost of running your own SOC
 * @param {number} analystCount
 * @param {number} analystCompensation
 * @param {number} endpoints
 * @param {number} exchangeRate
 * @returns {number}
 */
function calculateCostOfOwnSOC(analystCount, analystCompensation, endpoints, exchangeRate = 1) {
    const staffingIsValid = Number.isFinite(analystCount) && Number.isFinite(analystCompensation);
    const staffingCost = staffingIsValid ? analystCount * analystCompensation : 0;
    const infrastructureCost = calculateInfrastructureCost(endpoints, exchangeRate);
    return staffingCost + infrastructureCost;
}

/**
 * Calculate ThreatDown MDR annual cost
 * @param {number} endpoints
 * @param {number} mdrPricePerEndpoint
 * @returns {number}
 */
function calculateThreatdownMDRCost(endpoints, mdrPricePerEndpoint) {
    return endpoints * mdrPricePerEndpoint; // Annual cost assuming price is per endpoint per year
}

/**
 * Calculate savings between SOC and MDR
 * @param {number} ownSocCost
 * @param {number} mdrCost
 * @returns {number}
 */
function calculateSavings(ownSocCost, mdrCost) {
    return ownSocCost - mdrCost;
}

/**
 * Parse a numeric input value (allowing commas, spaces, currency symbols)
 * @param {string} value
 * @returns {number} Parsed number or NaN if invalid
 */
function parseNumber(value) {
    if (typeof value !== 'string') return NaN;
    const cleaned = value.replace(/[$,£€\s]/g, '').trim();
    if (!cleaned) return NaN;
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : NaN;
}

/**
 * Format number as currency using the selected country's currencyCode
 * @param {number} value
 * @param {Country} country
 * @returns {string}
 */
function formatCurrency(value, country) {
    const cfg = COUNTRY_CONFIGS[country];
    if (!cfg) return String(value);

    if (typeof Intl !== 'undefined' && Intl.NumberFormat) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: cfg.currencyCode,
            maximumFractionDigits: 0
        }).format(value);
    }

    // Fallback for older browsers
    return cfg.currencySymbol + value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/**
 * Format number with commas for display in input fields
 * @param {number} value
 * @returns {string}
 */
function formatNumberWithCommas(value) {
    if (!Number.isFinite(value)) return '';
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/**
 * Clamp a numeric value between the provided bounds
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clampNumber(value, min, max) {
    if (!Number.isFinite(value)) return NaN;
    let result = value;
    if (typeof min === 'number') {
        result = Math.max(result, min);
    }
    if (typeof max === 'number') {
        result = Math.min(result, max);
    }
    return result;
}

/**
 * Basic debounce utility to throttle rapid-fire input events
 * @param {Function} fn
 * @param {number} delay
 * @returns {Function}
 */
function debounce(fn, delay = 800) {
    let timeoutId;
    return (...args) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            timeoutId = null;
            fn(...args);
        }, delay);
    };
}

// ----- Calculator Class -----

class MDRROICalculator {
    /**
     * @param {Object} selectors - Element ID selectors
     */
    constructor(selectors) {
        this.selectors = selectors;
        this.chartHeightFrame = null;
        this.lastChartCosts = { soc: 0, mdr: 0 };
        this.handleResize = () => {
            this.scheduleChartHeightUpdate(this.lastChartCosts.soc, this.lastChartCosts.mdr);
        };

        // Initialize state with defaults
        const cfg = COUNTRY_CONFIGS[DEFAULTS.country];
        const safeRevenue = clampNumber(
            DEFAULTS.annualRevenue,
            INPUT_LIMITS.annualRevenue.min,
            INPUT_LIMITS.annualRevenue.max
        );
        const safeEndpoints = clampNumber(
            DEFAULTS.endpoints,
            INPUT_LIMITS.endpoints.min,
            INPUT_LIMITS.endpoints.max
        );
        const defaultAnalystCountValue = clampNumber(
            defaultAnalystCount(safeEndpoints),
            INPUT_LIMITS.analystCount.min,
            INPUT_LIMITS.analystCount.max
        );
        const initialAnalystComp = clampNumber(
            cfg.loadedSalary,
            INPUT_LIMITS.analystCompensation.min,
            INPUT_LIMITS.analystCompensation.max
        );
        this.state = {
            country: DEFAULTS.country,
            riskType: DEFAULTS.riskType,
            annualRevenue: Number.isFinite(safeRevenue) ? safeRevenue : INPUT_LIMITS.annualRevenue.min,
            endpoints: Number.isFinite(safeEndpoints) ? safeEndpoints : INPUT_LIMITS.endpoints.min,
            analystCount: Number.isFinite(defaultAnalystCountValue) ? defaultAnalystCountValue : INPUT_LIMITS.analystCount.min,
            analystCompensation: Number.isFinite(initialAnalystComp) ? initialAnalystComp : INPUT_LIMITS.analystCompensation.min,
            recommendedAnalystCount: Number.isFinite(defaultAnalystCountValue) ? defaultAnalystCountValue : NaN,
            analystCountOverridden: false,
            analystCompOverridden: false
        };

        this.cacheElements();
        this.initializeInputs();
        this.attachListeners();
        this.updateOutputs();

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.handleResize);
        }
    }

    /**
     * Cache DOM element references
     */
    cacheElements() {
        const s = this.selectors;
        this.$ = {
            form: document.getElementById(s.form),
            mediumRisk: document.getElementById(s.mediumRisk),
            highRisk: document.getElementById(s.highRisk),
            revenue: document.getElementById(s.revenue),
            revenueCurrency: document.getElementById(s.revenueCurrency),
            country: document.getElementById(s.country),
            endpoints: document.getElementById(s.endpoints),
            recAnalysts: document.getElementById(s.recAnalysts),
            analystComp: document.getElementById(s.analystComp),
            analystWarningIcon: document.getElementById(s.analystWarningIcon),
            analystWarningMessage: document.getElementById(s.analystWarningMessage),
            analystWarningRecommended: document.getElementById(s.analystWarningRecommended),
            // Output elements
            outCostOfBreach: document.getElementById(s.outCostOfBreach),
            outSocCost: document.getElementById(s.outSocCost),
            outSocCostChart: document.getElementById(s.outSocCostChart),
            outMdrCost: document.getElementById(s.outMdrCost),
            outMdrCostChart: document.getElementById(s.outMdrCostChart),
            outMdrPrice: document.getElementById(s.outMdrPrice),
            outSavings: document.getElementById(s.outSavings),
            chartSocBar: document.getElementById(s.chartSocBar),
            chartMdrBar: document.getElementById(s.chartMdrBar)
        };
    }

    /**
     * Set default values in form inputs
     */
    initializeInputs() {
        const $ = this.$;
        const s = this.state;

        if ($.revenue) $.revenue.value = formatNumberWithCommas(s.annualRevenue);
        if ($.endpoints) $.endpoints.value = Number.isFinite(s.endpoints) ? s.endpoints : '';
        if ($.recAnalysts) $.recAnalysts.value = Number.isFinite(s.analystCount) ? s.analystCount : '';
        if ($.analystComp) $.analystComp.value = formatNumberWithCommas(s.analystCompensation);

        // Set initial currency-dependent displays
        this.updateMdrPrice();
    }

    /**
     * Attach event listeners to form inputs
     */
    attachListeners() {
        const $ = this.$;

        const debouncedRevenueInput = debounce((value) => {
            const limits = INPUT_LIMITS.annualRevenue;
            const trimmed = typeof value === 'string' ? value.trim() : '';

            if (trimmed === '') {
                this.state.annualRevenue = NaN;
                if ($.revenue) $.revenue.value = '';
                this.updateOutputs();
                return;
            }

            const parsed = parseNumber(trimmed);
            if (!Number.isFinite(parsed)) {
                this.state.annualRevenue = NaN;
                this.updateOutputs();
                return;
            }

            const clamped = clampNumber(parsed, limits.min, limits.max);
            this.state.annualRevenue = clamped;
            if ($.revenue) {
                $.revenue.value = formatNumberWithCommas(clamped);
            }
            this.updateOutputs();
        });

        const debouncedEndpointsInput = debounce((value) => {
            const limits = INPUT_LIMITS.endpoints;
            const trimmed = typeof value === 'string' ? value.trim() : '';

            if (trimmed === '') {
                this.state.endpoints = NaN;
                this.state.recommendedAnalystCount = NaN;
                this.state.analystCountOverridden = false;
                if ($.endpoints) $.endpoints.value = '';
                this.state.analystCount = NaN;
                if ($.recAnalysts) $.recAnalysts.value = '';
                this.updateOutputs();
                return;
            }

            const parsed = parseInt(trimmed, 10);
            if (!Number.isFinite(parsed)) {
                this.state.endpoints = NaN;
                this.updateOutputs();
                return;
            }

            const clamped = clampNumber(parsed, limits.min, limits.max);
            this.state.endpoints = clamped;
            if ($.endpoints) {
                $.endpoints.value = clamped;
            }

            const recommended = defaultAnalystCount(clamped);
            const boundedRecommended = Number.isNaN(recommended)
                ? NaN
                : clampNumber(
                    recommended,
                    INPUT_LIMITS.analystCount.min,
                    INPUT_LIMITS.analystCount.max
                );
            this.state.recommendedAnalystCount = boundedRecommended;

            if (!Number.isNaN(boundedRecommended)) {
                this.state.analystCountOverridden = false;
                this.state.analystCount = boundedRecommended;
                if ($.recAnalysts) $.recAnalysts.value = boundedRecommended;
            }
            this.updateOutputs();
        });

        const debouncedAnalystInput = debounce((value) => {
            const limits = INPUT_LIMITS.analystCount;
            const trimmed = typeof value === 'string' ? value.trim() : '';

            this.state.analystCountOverridden = true;

            if (trimmed === '') {
                this.state.analystCount = NaN;
                if ($.recAnalysts) $.recAnalysts.value = '';
                this.updateOutputs();
                return;
            }

            const parsed = parseInt(trimmed, 10);
            if (!Number.isFinite(parsed)) {
                this.state.analystCount = NaN;
                this.updateOutputs();
                return;
            }

            const clamped = clampNumber(parsed, limits.min, limits.max);
            this.state.analystCount = clamped;
            if ($.recAnalysts) {
                $.recAnalysts.value = clamped;
            }
            this.updateOutputs();
        });

        const debouncedAnalystCompInput = debounce((value) => {
            const limits = INPUT_LIMITS.analystCompensation;
            const trimmed = typeof value === 'string' ? value.trim() : '';

            this.state.analystCompOverridden = true;

            if (trimmed === '') {
                this.state.analystCompensation = NaN;
                if ($.analystComp) $.analystComp.value = '';
                this.updateOutputs();
                return;
            }

            const parsed = parseNumber(trimmed);
            if (!Number.isFinite(parsed)) {
                this.state.analystCompensation = NaN;
                this.updateOutputs();
                return;
            }

            const clamped = clampNumber(parsed, limits.min, limits.max);
            this.state.analystCompensation = clamped;
            if ($.analystComp) {
                $.analystComp.value = formatNumberWithCommas(clamped);
            }
            this.updateOutputs();
        });

        // Risk type radio buttons
        $.mediumRisk?.addEventListener('change', () => {
            this.state.riskType = 'Medium';
            this.updateOutputs();
        });

        $.highRisk?.addEventListener('change', () => {
            this.state.riskType = 'High';
            this.updateOutputs();
        });

        // Revenue input
        $.revenue?.addEventListener('input', (e) => {
            debouncedRevenueInput(e.target.value);
        });

        // Country select
        $.country?.addEventListener('change', (e) => {
            this.state.country = COUNTRY_SELECT_MAP[e.target.value] || 'USA';
            this.updateCurrencySymbol();
            this.updateMdrPrice();
            this.updateAnalystCompDefault();
            this.updateOutputs();
        });

        // Endpoints input
        $.endpoints?.addEventListener('input', (e) => {
            debouncedEndpointsInput(e.target.value);
        });

        // Recommended analysts input
        $.recAnalysts?.addEventListener('input', (e) => {
            debouncedAnalystInput(e.target.value);
        });

        // Analyst compensation input
        $.analystComp?.addEventListener('input', (e) => {
            debouncedAnalystCompInput(e.target.value);
        });
    }

    /**
     * Update currency symbol display based on selected country
     */
    updateCurrencySymbol() {
        const cfg = COUNTRY_CONFIGS[this.state.country];
        if (this.$.revenueCurrency && cfg) {
            this.$.revenueCurrency.textContent = cfg.currencySymbol;
        }
    }

    /**
     * Update MDR price per endpoint display based on selected country
     */
    updateMdrPrice() {
        const cfg = COUNTRY_CONFIGS[this.state.country];
        if (this.$.outMdrPrice && cfg) {
            this.$.outMdrPrice.textContent = cfg.currencySymbol + cfg.mdrPricePerEndpoint;
        }
    }

    /**
     * Update analyst compensation default based on selected country
     */
    updateAnalystCompDefault() {
        if (!this.state.analystCompOverridden) {
            const cfg = COUNTRY_CONFIGS[this.state.country];
            const limits = INPUT_LIMITS.analystCompensation;
            const salary = clampNumber(cfg?.loadedSalary ?? NaN, limits.min, limits.max);
            this.state.analystCompensation = Number.isFinite(salary) ? salary : limits.min;
            if (this.$.analystComp) {
                this.$.analystComp.value = formatNumberWithCommas(this.state.analystCompensation);
            }
        }
    }

    /**
     * Recalculate and update all output displays
     */
    updateOutputs() {
        const s = this.state;
        const cfg = COUNTRY_CONFIGS[s.country];

        // Calculate values
        const breach = Number.isFinite(s.annualRevenue)
            ? calculateCostOfBreach(s.annualRevenue, s.riskType)
            : 0;

        const exchangeRate = cfg?.exchangeRate ?? 1;
        const soc = calculateCostOfOwnSOC(s.analystCount, s.analystCompensation, s.endpoints, exchangeRate);

        const mdr = (Number.isFinite(s.endpoints) && cfg)
            ? calculateThreatdownMDRCost(s.endpoints, cfg.mdrPricePerEndpoint)
            : 0;

        const savings = calculateSavings(soc, mdr);

        // Update DOM
        this.setTextContent(formatCurrency(breach, s.country), this.$.outCostOfBreach);
        const formattedSoc = formatCurrency(soc, s.country);
        this.setTextContent(formattedSoc, this.$.outSocCost, this.$.outSocCostChart);
        const formattedMdr = formatCurrency(mdr, s.country);
        this.setTextContent(formattedMdr, this.$.outMdrCost, this.$.outMdrCostChart);
        this.setTextContent(formatCurrency(savings, s.country), this.$.outSavings);

        this.scheduleChartHeightUpdate(soc, mdr);
        this.updateAnalystWarning();
    }

    /**
     * Safely set text content for provided elements
     * @param {string} value
     * @param {...HTMLElement|null} elements
     */
    setTextContent(value, ...elements) {
        elements.forEach((el) => {
            if (el) {
                el.textContent = value;
            }
        });
    }

    /**
     * Queue proportional chart height update using SOC bar as baseline
     * @param {number} socCost
     * @param {number} mdrCost
     */
    scheduleChartHeightUpdate(socCost, mdrCost) {
        this.lastChartCosts = { soc: socCost, mdr: mdrCost };

        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
            this.applyChartHeights();
            return;
        }

        if (this.chartHeightFrame && typeof window.cancelAnimationFrame === 'function') {
            window.cancelAnimationFrame(this.chartHeightFrame);
        }

        this.chartHeightFrame = window.requestAnimationFrame(() => {
            this.chartHeightFrame = null;
            this.applyChartHeights();
        });
    }

    /**
     * Apply last known ratio to ThreatDown MDR bar
     */
    applyChartHeights() {
        const { soc, mdr } = this.lastChartCosts;
        const socBar = this.$.chartSocBar;
        const mdrBar = this.$.chartMdrBar;
        if (!socBar || !mdrBar) return;

        const socHeight = socBar.getBoundingClientRect().height;
        const validSoc = Number.isFinite(soc) && soc > 0;
        const validMdr = Number.isFinite(mdr) && mdr >= 0;

        if (!validSoc || !validMdr || socHeight <= 0) {
            mdrBar.style.height = '';
            return;
        }

        const ratio = Math.max(mdr / soc, 0);
        const targetHeight = socHeight * ratio;
        mdrBar.style.height = `${targetHeight}px`;
    }

    /**
     * Toggle analyst warning visibility based on recommended staffing levels
     */
    updateAnalystWarning() {
        const recommended = this.state.recommendedAnalystCount;
        const analysts = this.state.analystCount;
        const showWarning = Number.isFinite(recommended) && Number.isFinite(analysts) && analysts < recommended;

        const icon = this.$.analystWarningIcon;
        const message = this.$.analystWarningMessage;
        const recommendedEl = this.$.analystWarningRecommended;

        if (recommendedEl && Number.isFinite(recommended)) {
            recommendedEl.textContent = formatNumberWithCommas(recommended);
        } else if (recommendedEl) {
            recommendedEl.textContent = '';
        }

        if (icon) {
            icon.classList.toggle('opacity-0', !showWarning);
            icon.classList.toggle('opacity-100', showWarning);
        }

        if (message) {
            message.classList.toggle('hidden', !showWarning);
        }
    }
}

// ----- Initialize Calculator on DOM Ready -----
document.addEventListener('DOMContentLoaded', () => {
    window.mdrRoiCalculator = new MDRROICalculator({
        form: 'calculator-form',
        mediumRisk: 'medium-risk',
        highRisk: 'high-risk',
        revenue: 'revenue',
        revenueCurrency: 'revenue-currency',
        country: 'country',
        endpoints: 'endpoints',
        recAnalysts: 'rec-analysts',
        analystComp: 'analyst-compensation',
        analystWarningIcon: 'analyst-warning-icon',
        analystWarningMessage: 'analyst-warning-message',
        analystWarningRecommended: 'analyst-warning-recommended',
        // Output element IDs
        outCostOfBreach: 'output-cost-breach',
        outSocCost: 'output-soc-cost',
        outSocCostChart: 'output-soc-cost-chart',
        outMdrCost: 'output-mdr-cost',
        outMdrCostChart: 'output-mdr-cost-chart',
        outMdrPrice: 'output-mdr-price',
        outSavings: 'output-savings',
        chartSocBar: 'chart-bar-soc',
        chartMdrBar: 'chart-bar-mdr'
    });
});